import { Api, TokenInfoRequest, TokenInfoResponse } from 'vopay';
import { useEffect, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import vopayApi from '../../services/api.config';
import { useDataFetchIndicator } from '..';

type HookProps = {
  api: Api;
};

type ReturnTuple = [
  TokenInfoResponse,
  (params: TokenInfoRequest) => void,
  () => void,
  (params: TokenInfoRequest) => Promise<TokenInfoResponse>,
];

const defaults: TokenInfoResponse = {
  Success: false,
  ErrorMessage: '',
  AccountSelectionMethod: '',
  AccountHolderType: '',
  BGColor: '',
  ClientName: '',
  ConsentMessage: '',
  Country: '',
  Currency: 'CAD',
  FlinksInstitutionID: 0,
  ForegroundColor: '',
  Language: '',
  PaymentSelectionMethod: '',
  RedirectMethod: '',
  RedirectURL: '',
  TermsAndConditions: '',
  HasLogin: true,
  WithTransactions: false,
  Version: '',
  DebitAuthority: '',
  RequireDebitAuthorityAgreement: false,
  BrandingDetails: {},
  DarkMode: false,
  JWT: '',
  AgreementURL: '',
  AcceptedCardBrands: [],
  CardTypeValidation: false,
  DebitAgreementBusinessEnUrl: '',
  DebitAgreementBusinessFrUrl: '',
  DebitAgreementIndividualEnUrl: '',
  DebitAgreementIndividualFrUrl: '',
  PrivacyPolicyEnURL: '',
  PrivacyPolicyFrURL: '',
  TermsOfServiceEnURL: '',
  TermsOfServiceFrURL: '',
};

function useFetchiQ11TokenInfo({ api }: HookProps): ReturnTuple {
  const { t } = useTranslation('translations', { keyPrefix: 'Common' });
  const source = axios.CancelToken.source();

  const [response, setResponse] = useState<TokenInfoResponse>(defaults);
  const [shouldSendRequest, setSendRequest] = useState(false);
  const [, setDataFetchingInfo] = useDataFetchIndicator();

  const [request, setRequest] = useState<TokenInfoRequest>({
    ForOnboarding: false,
  });

  useEffect(() => {
    const sendRequest = async () => {
      setDataFetchingInfo(true, `${t('defaultLoading')}`, 'fullScreen');

      const res = await api.fetchiQ11TokenInfo(request, source);

      setResponse(res);
      setSendRequest(false);
      setDataFetchingInfo(false);

      return () => {};
    };

    if (shouldSendRequest) void sendRequest();

    return () => {
      source.cancel();
    };
  }, [shouldSendRequest]);

  return [
    response,
    (params: TokenInfoRequest) => {
      setRequest(params);
      setSendRequest(true);
    },
    () => {
      setResponse(defaults);
      setSendRequest(false);
    },
    async (params: TokenInfoRequest): Promise<TokenInfoResponse> => {
      const asyncSource = axios.CancelToken.source();
      const res = await api.fetchiQ11TokenInfo(params, asyncSource);
      setResponse(res);

      return res;
    },
  ];
}

export default vopayApi.withApiHook<HookProps, ReturnTuple>(useFetchiQ11TokenInfo);
